import type { Messages } from '@/types'

const messages: Messages = {
    common: {
        actions: {
            add: 'Pridať',
            edit: 'Upraviť',
            delete: 'Zmazať',
            cancel: 'Zrušiť',
            back: 'Späť',
            save: 'Uložiť',
            update: 'Aktualizovať',
            reset: 'Resetovať',
            refresh: 'Obnoviť',
            backToList: '@:common.back do zoznamu',
            resetDefaults: '@:common.reset na predvolené',
            confirm: 'Potvrdiť',
            proceed: 'Pokračovať',
            close: 'Zavrieť',
            apply: 'Použiť',
            search: 'Hľadať',
            filter: 'Filtrovať',
            clear: 'Vyčistiť',
            select: 'Vybrať',
            upload: 'Nahrať',
            download: 'Stiahnuť',
            view: 'Zobraziť',
            sort: 'Zoradiť',
            seeMore: 'Zobraziť viac'
        },
        status: {
            success: 'Úspech',
            error: 'Chyba',
            loading: 'Načítava sa...'
        },
        messages: {
            deleteConfirm: 'Naozaj chcete zmazať tohto používateľa?',
            generalError: 'Niečo sa pokazilo! Skúste to znova',
            addSuccess: 'Záznam bol úspešne pridaný!',
            updateSuccess: 'Záznam bol úspešne aktualizovaný!',
        },
        placeholder: {
            avatar: '?'
        },
        labels: {
            loading: 'Načítava sa',
            noResults: 'Nenašli sa žiadne výsledky',
            noData: 'Žiadne údaje nie sú k dispozícii',
            selected: 'Vybrané',
            required: 'Povinné',
            optional: 'Voliteľné',
            search: 'Hľadať...',
            filter: 'Filtrovať...',
            from: 'Od',
            to: 'Do',
            date: 'Dátum',
            status: 'Stav',
            item: 'Položka {index}',
            addItem: 'Pridať položku',
            removeItem: 'Odstrániť položku',
            skipToMain: 'Prejsť na hlavný obsah',
            openNavigation: 'Otvoriť navigáciu',
            closeNavigation: 'Zavrieť navigáciu',
            mainNavigation: 'Hlavná navigácia',
            moveUp: 'Presunúť nahor',
            moveDown: 'Presunúť nadol',
            and: 'a'
        },
        errors: {
            upload: 'Nepodarilo sa nahrať súbor',
            download: 'Nepodarilo sa stiahnuť súbor',
            generic: 'Vyskytla sa chyba',
            notFound: 'Nenájdené',
            unauthorized: 'Neautorizovaný prístup',
            validation: 'Prosím skontrolujte svoj vstup'
        },
        validation: {
            required: 'Toto pole je povinné',
            email: 'Neplatná emailová adresa',
            minLength: 'Minimálna dĺžka je {length}',
            maxLength: 'Maximálna dĺžka je {length}',
            min: 'Minimálna hodnota je {min}',
            max: 'Maximálna hodnota je {max}',
            matches: 'Neplatný formát',
            unique: 'Hodnota musí byť jedinečná',
            confirm: 'Heslá sa nezhodujú'
        }
    },
    pages: {
        users: {
            title: 'Používatelia',
            description: 'Spravovať systémových používateľov',
            actions: {
                invite: 'Pozvať používateľa',
                resetPassword: 'Obnoviť heslo'
            }
        },
        settings: {
            title: 'Nastavenia',
            actions: {
                resetDefaults: 'Obnoviť predvolené'
            }
        },
        reports: {
            title: 'Správy',
            description: 'Generovať a stiahnuť správy',
            actions: {
                generateReport: 'Generovať správu',
                downloadCsv: 'Stiahnuť CSV',
                startDate: 'Dátum začiatku',
                endDate: 'Dátum konca',
                apps: 'Aplikácie',
            },
            error: {
                generation: 'Nepodarilo sa vygenerovať správu',
                download: 'Nepodarilo sa stiahnuť správu'
            }
        }
    },
    navigation: {
        home: 'Domov',
        library: 'Knižnica',
        search: 'Hľadať',
        login: 'Prihlásiť sa',
        signInToGetPremium: 'Získať Premium',
        subscribe: 'Prihlásiť sa',
        support: 'Podpora',
        settings: 'Nastavenia',
        logout: 'Odhlásiť sa'
    },
    settings: {
        title: 'Nastavenia',
        description: 'Spravujte nastavenia svojho účtu, preferencie a informácie o profile.',
        sections: 'Sekcie nastavení',
        menu: {
            profile: 'Profil',
            account: 'Účet',
            general: 'Všeobecné',
            purchases: 'Nákupy',
            activity: 'Aktivita'
        },
        profile: {
            title: 'Profil',
            changeImage: 'Zmeniť obrázok',
            updateProfile: 'Aktualizovať profil',
            changePassword: 'Zmeniť heslo',
            updatePassword: 'Aktualizovať heslo',
            fields: {
                fullName: 'Celé meno',
                emailAddress: 'Emailová adresa',
                dateOfBirth: 'Dátum narodenia',
                currentPassword: 'Súčasné heslo',
                newPassword: 'Nové heslo',
                confirmNewPassword: 'Potvrdiť nové heslo'
            },
            toasts: {
                imageUpdated: 'Obrázok aktualizovaný',
                imageUpdatedMessage: 'Váš profilový obrázok bol úspešne aktualizovaný',
                uploadFailed: 'Nahrávanie zlyhalo',
                profileUpdated: 'Profil aktualizovaný',
                profileUpdatedMessage: 'Váš profil bol úspešne aktualizovaný',
                passwordUpdated: 'Heslo aktualizované',
                passwordUpdatedMessage: 'Vaše heslo bolo úspešne aktualizované',
                passwordError: 'Chyba hesla',
                passwordMismatch: 'Heslá sa nezhodujú',
                passwordTooWeak: 'Heslo je príliš slabé'
            }
        },
        account: {
            title: 'Účet',
            accountInformation: 'Informácie o účte',
            emailAddress: 'Emailová adresa',
            memberSince: 'Člen od',
            redeemPromoCode: 'Uplatniť zľavový kód',
            redeemPromoCodeDescription: 'Máte propagačný alebo darčekový kód? Zadajte ho tu a aplikuje sa na váš účet alebo ďalšie predplatné.',
            subscription: {
                title: 'Predplatné',
                currentPlan: 'Súčasný plán',
                premiumSubscription: 'Prémiové predplatné',
                nextRenewal: 'Ďalšie obnovenie:',
                accessUntil: 'Prístup do:',
                manageInAppStore: 'Vaše predplatné bolo zakúpené cez App Store. Na zrušenie alebo zmenu otvorte Predplatné v nastaveniach Apple ID na zariadení, cez ktoré ste sa predplatili.',
                manageInPlayStore: 'Vaše predplatné bolo zakúpené cez Google Play. Na zrušenie alebo zmenu otvorte Predplatné v Google Play Store na zariadení, cez ktoré ste sa predplatili.',
                notSubscribed: 'Momentálne nie ste prihlásení na žiadny plán.',
                subscribeNow: 'Prihlásiť sa teraz',
                findOutMore: 'Dozvedieť sa viac',
                cancelSubscription: 'Zrušiť predplatné',
                cancelSubscriptionTitle: 'Zrušiť predplatné',
                cancelSubscriptionMessage: 'Naozaj chcete zrušiť svoje predplatné? Stratíte prístup k prémiovému obsahu na konci svojej súčasnej fakturačnej periódy.',
                keepSubscription: 'Ponechať predplatné',
                toasts: {
                    subscriptionActivated: 'Predplatné aktivované',
                    subscriptionActivatedMessage: 'Vitajte v Premium!',
                    subscriptionFailed: 'Predplatné zlyhalo',
                    paymentMethodUpdated: 'Platobná metóda aktualizovaná',
                    paymentMethodUpdatedMessage: 'Vaše platobné informácie boli úspešne aktualizované',
                    subscriptionCancelled: 'Predplatné zrušené',
                    subscriptionCancelledMessage: 'Vaše predplatné sa skončí v súčasnej fakturačnej perióde',
                    cancellationFailed: 'Zrušenie zlyhalo'
                }
            },
            closeAccount: {
                title: 'Zavrieť účet',
                requestDescription: 'Požiadať o trvalé zatvorenie vášho účtu. Túto akciu nie je možné vrátiť späť.',
                requestButton: 'Požiadať o zatvorenie účtu',
                confirmationTitle: 'Naozaj chcete deaktivovať svoj účet? Toto bude:',
                confirmationItems: {
                    cancelSubscription: 'Zrušiť vaše aktívne predplatné (ak existuje)',
                    removeCommunications: 'Odstrániť vás zo všetkých komunikácií',
                    scheduleDeletion: 'Naplánovať trvalé vymazanie vašich údajov po 30 dňoch'
                },
                confirmationWarning: 'Môžete zrušiť svoju žiadosť o deaktiváciu kedykoľvek do 30 dní. Po tomto období bude váš účet a všetky súvisiace údaje trvalo vymazané.',
                confirmationCheckbox: 'Rozumiem, že môj účet bude trvalo vymazaný po 30 dňoch',
                deactivateAccount: 'Deaktivovať účet',
                cancel: 'Zrušiť',
                scheduledTitle: 'Deaktivácia účtu naplánovaná',
                scheduledMessage: 'Váš účet je naplánovaný na trvalé vymazanie dňa',
                scheduledWarning: 'Počas tohto obdobia sa nebudete môcť prihlásiť do svojho účtu. Ak zmeníte názor, môžete zrušiť proces deaktivácie.',
                cancelDeactivation: 'Zrušiť deaktiváciu účtu',
                toasts: {
                    deactivationRequested: 'Deaktivácia účtu požiadaná',
                    deactivationRequestedMessage: 'Váš účet bude trvalo vymazaný za 30 dní',
                    deactivationFailed: 'Deaktivácia zlyhala',
                    deactivationCancelled: 'Deaktivácia zrušená',
                    deactivationCancelledMessage: 'Váš účet už nie je naplánovaný na vymazanie'
                }
            }
        },
        purchases: {
            title: 'História nákupov',
            noPurchases: 'Nenašli sa žiadne nákupy',
            invoice: 'Faktúra'
        },
        activity: {
            title: 'Časová os aktivity',
            dateRange: 'Rozsah dátumov',
            activityType: 'Typ aktivity',
            noActivities: 'Nenašli sa žiadne aktivity',
            resume: 'Pokračovať',
            loadMore: 'Načítať viac',
            filters: {
                last7Days: 'Posledných 7 dní',
                last30Days: 'Posledných 30 dní',
                lastYear: 'Minulý rok',
                allTime: 'Celý čas',
                allActivities: 'Všetky aktivity',
                listening: 'Počúvanie',
                purchases: 'Nákupy',
                likes: 'Páčia sa',
                bookmarks: 'Záložky'
            },
            types: {
                listenedTo: 'Počúvané {title}',
                purchased: 'Zakúpené {title}',
                liked: 'Páčilo sa {title}',
                bookmarked: 'Záložka {title}'
            }
        },
        appSettings: {
            title: 'Nastavenia aplikácie',
            darkMode: 'Tmavý režim',
            language: 'Jazyk',
            languageOptions: {
                english: 'Angličtina',
                spanish: 'Španielčina',
                french: 'Francúzština',
                german: 'Nemčina',
                italian: 'Taliančina',
                portuguese: 'Portugalčina',
                dutch: 'Holandčina',
                slovak: 'Slovenčina',
                russian: 'Ruština',
                arabic: 'العربية'
            }
        },
        notifications: {
            title: 'Notifikácie',
            emailNotifications: 'Emailové notifikácie',
            emailNotificationsHint: 'Dostávať aktualizácie e-mailom',
            pushNotifications: 'Push notifikácie',
            pushNotificationsHint: 'Dostávať push notifikácie',
            updatePreferences: 'Aktualizovať preferencie'
        },
        toasts: {
            themeUpdated: 'Téma aktualizovaná',
            themeUpdatedMessage: 'Vaša preferencia témy bola uložená',
            languageUpdated: 'Jazyk aktualizovaný',
            languageUpdatedMessage: 'Vaša preferencia jazyka bola uložená',
            preferencesUpdated: 'Preferencie aktualizované',
            preferencesUpdatedMessage: 'Vaše preferencie notifikácií boli uložené',
            updateFailed: 'Aktualizácia zlyhala',
            updateFailedMessage: 'Nepodarilo sa aktualizovať nastavenie',
            updateFailedNotificationMessage: 'Nepodarilo sa aktualizovať preferencie notifikácií'
        }
    },
    auth: {
        login: {
            title: 'Prihlásiť sa',
            description: 'Prihláste sa do svojho účtu Heard pre prístup k vašej knižnici a prémiovému obsahu',
            signIn: 'Prihlásiť sa',
            signInToAccount: 'Prihláste sa do svojho účtu',
            noAccount: 'Nemáte účet?',
            signUp: 'Zaregistrovať sa',
            continueAsGuest: 'Pokračovať ako hosť',
            or: 'Alebo',
            agreeToTerms: 'Prihlásením súhlasíte s našimi',
            termsOfService: 'Podmienkami služby',
            privacyPolicy: 'Zásadami ochrany súkromia',
            forgotPassword: 'Zabudli ste heslo?'
        },
        register: {
            title: 'Registrovať sa',
            description: 'Zaregistrujte sa pre účet Heard pre prístup k prémiovému audio obsahu, vytvorenie osobnej knižnice a viac',
            createAccount: 'Vytvoriť účet',
            createYourAccount: 'Vytvorte svoj účet',
            orSignIn: 'Alebo',
            signIn: 'prihlásiť sa',
            backToHome: 'Späť domov',
            agreeToTerms: 'Vytvorením účtu súhlasíte s našimi',
            termsOfService: 'Podmienkami služby',
            privacyPolicy: 'Zásadami ochrany súkromia'
        },
        forms: {
            email: 'Emailová adresa',
            password: 'Heslo',
            confirmPassword: 'Potvrdiť heslo',
            fullName: 'Celé meno',
            rememberMe: 'Zapamätať si ma',
            signIn: 'Prihlásiť sa',
            createAccount: 'Vytvoriť účet'
        },
        toasts: {
            loginSuccess: 'Prihlásenie úspešné',
            loginFailed: 'Prihlásenie zlyhalo',
            registerSuccess: 'Účet bol úspešne vytvorený',
            registerFailed: 'Registrácia zlyhala'
        }
    },
    library: {
        title: 'Moja knižnica',
        description: 'Prístup k vašim nedávno prehratým, obľúbeným, zakúpeným, označeným a dokončeným titulom vo vašej osobnej knižnici.',
        sections: 'Sekcie knižnice',
        noItems: 'V tejto sekcii nie sú žiadne položky',
        sync: 'Synchronizovať knižnicu',
        tabs: {
            recent: 'Pokračovať',
            liked: 'Obľúbené',
            purchased: 'Zakúpené',
            bookmarked: 'Záložky',
            finished: 'Počúvať znova',
            finishedWatch: 'Pozerať znova',
            finishedRead: 'Čítať znova'
        }
    },
    home: {
        title: 'Domov',
        error: {
            loadFailed: 'Nepodarilo sa načítať obsah'
        }
    },
    player: {
        chapters: 'Kapitoly',
        options: 'Možnosti',
        playbackSpeed: 'Rýchlosť prehrávania',
        share: 'Zdieľať',
        linkCopied: 'Odkaz skopírovaný',
        linkCopiedMessage: 'Odkaz na zdieľanie skopírovaný do schránky!',
        shareError: 'Chyba zdieľania',
        shareErrorMessage: 'Nepodarilo sa skopírovať odkaz na zdieľanie. Skúste to znova.',
        bookmarkAdded: 'Úspech',
        bookmarkAddedMessage: 'Záložka pridaná!',
        jumpedToBookmark: 'Úspech',
        jumpedToBookmarkMessage: 'Preskočené na záložku!',
        likeError: 'Chyba obľúbenia',
        likeErrorMessage: 'Nepodarilo sa aktualizovať stav obľúbenia. Skúste to znova.',
        bookmarkError: 'Chyba záložky',
        bookmarkErrorMessage: 'Nepodarilo sa aktualizovať záložku. Skúste to znova.',
        favorites: {
            add: 'Pridať do obľúbených',
            remove: 'Odstrániť z obľúbených'
        },
        unknownChapter: 'Neznáma kapitola',
        unknownTitle: 'Neznámy titul',
        unknownAuthor: 'Neznámy autor'
    },
    details: {
        starring: 'V hlavnej úlohe:',
        author: 'Autor',
        narrator: 'Rozprávač',
        pause: 'Pozastaviť',
        purchased: 'Zakúpené',
        continueReading: 'Pokračovať v čítaní',
        continuePlaying: 'Pokračovať v prehrávaní',
        play: 'Prehrať',
        loading: 'Načítava sa...',
        noChapters: 'Žiadne kapitoly nie sú k dispozícii',
        chapterFreeLabel: 'Zadarmo',
        listen: 'Počúvať',
        purchaseSuccess: 'Nákup úspešný',
        purchaseSuccessMessage: '{title} bol pridaný do vašej knižnice',
        subscriptionSuccess: 'Predplatné úspešné',
        subscriptionSuccessMessage: 'Vitajte v Heard Premium!',
        subscriptionFailed: 'Predplatné zlyhalo',
        subscriptionErrorMessage: 'Vyskytla sa chyba pri spracovaní vášho predplatného.',
        info: 'Info',
        more: 'Viac',
        episodes: 'Epizódy',
        about: 'O diele',
        series: 'Séria',
        producedBy: 'Produkcia',
        recommendations: 'Môže sa vám páčiť aj',
        moreFromCategory: 'Viac z kategórie {name}',
        moreFromAuthor: 'Viac od {name}',
        moreFromCast: 'Viac od {name}',
        moreFromSeries: 'Viac zo série {name}',
        noRelated: 'Momentálne nie sú dostupné súvisiace tituly.',
        ageVerification: {
            title: 'Len pre dospelých',
            message: 'Tento obsah je len pre dospelých. Potvrďte, že chcete pokračovať.',
            minAgeHint: 'Na zobrazenie tohto titulu musíte mať aspoň {age} rokov.',
            confirm: 'Som dospelý, pokračovať',
            cancel: 'Zrušiť',
            missingDobTitle: 'Vyžaduje sa dátum narodenia',
            missingDobMessage: 'Pridajte dátum narodenia v profile, aby ste mohli tento obsah zobraziť.',
            underageTitle: 'Vekové obmedzenie',
            underageMessage: 'Na zobrazenie tohto obsahu musíte mať aspoň {age} rokov.',
        },
    },
    shareModal: {
        title: 'Zdieľať',
        copyLink: 'Kopírovať odkaz',
        copyLinkWithMessage: 'Kopírovať odkaz s správou',
        copyLinkWithMessageHint: 'Vrátane názvu a popisu',
        shareVia: 'Zdieľať cez...',
        shareViaHint: 'Aplikácie, správy, e-mail',
        linkCopied: 'Odkaz skopírovaný',
        linkCopiedMessage: 'Odkaz skopírovaný do schránky!',
        shared: 'Zdieľané',
        sharedMessage: 'Obsah bol úspešne zdieľaný'
    },
    contextMenu: {
        playFromBeginning: 'Prehrať od začiatku',
        markAsFinished: 'Označiť ako dokončené',
        addToFavorites: 'Pridať do obľúbených',
        removeFromFavorites: 'Odstrániť z obľúbených',
        share: 'Zdieľať',
        viewDetails: 'Zobraziť detaily',
        removeFromLibrary: 'Odstrániť z knižnice',
        continuePlaying: 'Pokračovať v prehrávaní',
        continueReading: 'Pokračovať v čítaní'
    },
    actions: {
        subscribe: 'Prihlásiť sa',
        signInToGetPremium: 'Získať Premium',
        purchase: 'Kúpiť',
        buy: 'Kúpiť',
        buyFor: 'Kúpiť za {price}',
        purchasedGoToLibrary: 'Zakúpené - Prejsť do knižnice',
        addToFavorites: 'Pridať do obľúbených',
        removeFromFavorites: 'Odstrániť z obľúbených',
        share: 'Zdieľať'
    },
    browse: {
        sortBy: 'Zoradiť podľa',
        sortOptions: {
            recent: 'Najnovšie',
            ascending: 'A-Z',
            descending: 'Z-A'
        }
    },

    legal: {
        companyNumber: 'IČO',
        privacyPolicy: 'Zásady ochrany súkromia',
        termsOfService: 'Podmienky používania',
        agreeToTermsSubscribe: 'Predplatením súhlasíte s našimi',
        operatedBy: '{appName} prevádzkuje {companyName}, {address}',
        faqs: 'FAQ'
    },
    support: {
        title: 'Podpora',
        description: 'Potrebujete pomoc s účtom, predplatným alebo počúvaním? Prezrite si FAQ a právne informácie alebo nás kontaktujte.',
        intro: 'Potrebujete pomoc s účtom, predplatným alebo počúvaním? Prezrite si FAQ a právne informácie nižšie alebo nás kontaktujte, ak potrebujete ďalšiu pomoc.',
        quickLinks: 'Rýchle odkazy',
        contactTitle: 'Kontaktovať podporu',
        contactDescription: 'Pošlite správu nášmu tímu podpory. Najprv si pozrite FAQ pre rýchle odpovede.',
        contactIntro: 'Ak potrebujete pomoc, sme tu pre vás. Na mnohé bežné otázky už máme odpovede v našom centre pomoci.',
        contactIntroFaqBefore: 'Pozrite si naše ',
        contactIntroFaqAfter: ' pre odpovede na bežné otázky. Ak stále potrebujete pomoc, vyplňte formulár a čo najskôr vás budeme kontaktovať.',
        contactSubmit: 'Odoslať správu',
        contactName: 'Meno',
        contactEmail: 'E-mail',
        contactSubject: 'Predmet',
        contactMessage: 'Správa',
        contactMessageHint: 'Popíšte svoj problém podrobne',
        backToSupport: 'Späť na podporu',
        pageLoading: 'Načítava sa…',
        pageNotAvailable: 'Táto stránka momentálne nie je k dispozícii.',
        unsupportedSection: 'Nepodporovaná sekcia obsahu: {name}',
        useContactFormBefore: 'Potrebujete nás kontaktovať? Použite náš ',
        contactSuccessTitle: 'Správa odoslaná',
        contactSuccessMessage: 'Vaša správa bola odoslaná. Čo najskôr vás budeme kontaktovať.',
        contactErrorTitle: 'Odoslanie zlyhalo',
        contactErrorMessage: 'Vašu správu sa nepodarilo odoslať. Skúste to znova.'
    },
    promo: {
        haveCode: 'Máte propagačný kód?',
        placeholder: 'Zadajte kód',
        apply: 'Aplikovať',
        remove: 'Odstrániť',
        applied: '{name} – {discount}',
        invalidCode: 'Neplatný alebo expirovaný kód.',
        expired: 'Tento kód vypršal.',
        notValidForPurchase: 'Tento kód nie je platný pre tento nákup.',
        free: 'Zadarmo',
        percentOff: '{value} % zľava',
        fixedOff: '{value} zľava',
        useWhenSubscribe: 'Túto ponuku použite pri predplatnom nižšie.',
        useWhenPurchase: 'Tento kód použite pri kúpe titulu.',
        accessGranted: 'Prístup bol pridaný k vášmu účtu.'
    }
}

export default messages

